import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AccountCustomizedTexts,
  AccountGotoAction,
  AccountStartParams,
  AccountStartupContext,
  AuthenticationContext,
  DisabledAccountRedirectService,
  filterForUpdatedTerms,
  GotoRegistrationComplete,
  GotoResetPasswordDone,
  GotoSetPasswordDone,
  IAuthenticationService,
  IOAuthPopupService,
  LinkAccountState,
  OAuthPopupService,
  OAuthProvider,
  SessionState,
  SetAuthFormEmail
} from '@platform-frontend/account';
import {
  AccountMigration,
  AppFlow,
  CHECKOUT_ACCOUNT_CUSTOMIZED_TEXTS,
  FlowStartupService,
  FlowState,
  MSG_ACADEMIC_CONFIRM_SIGN_UP_SUCCESS,
  MSG_COMMERCIAL_CHECKOUT_SIGN_UP_SUCCESS,
  PrepareFlow,
  RefreshSignInMessageParams,
  StartupConfigurationState
} from '@platform-frontend/checkout';
import { CheckoutLocale } from '@platform-frontend/checkout-drop-in';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { DateParams } from '@platform-frontend/transloco';
import { LoadingState } from '@platform-frontend/ui-tools';
import { ClearNotifications, CloseWindow, InvalidStateError, ShowInfoNotification, ShowSuccessNotification } from '@platform-frontend/util';
import { MonoTypeOperatorFunction, Observable, of, throwError } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { AppRoutesService } from '../app-routes.service';
import { WebCheckoutIntegrationService } from '../integration/web-checkout-integration.service';
import {
  MSG_ACCOUNT_MIGRATION_FROM_NOW,
  MSG_ACCOUNT_MIGRATION_NVV_FROM_NOW,
  MSG_ACCOUNT_MIGRATION_NVV_WITH_LAUNCH_DATE,
  MSG_ACCOUNT_MIGRATION_WITH_LAUNCH_DATE
} from '../notification-messages';
import { AccountAppStartParams } from './model/account-app-start-params';

@Injectable()
export class AuthenticationService implements IAuthenticationService {
  constructor(
    private appRoutesService: AppRoutesService,
    private disabledAccountRedirectService: DisabledAccountRedirectService,
    private flowStartupService: FlowStartupService,
    private oAuthPopupService: OAuthPopupService,
    private store: Store,
    private webCheckoutIntegrationService: WebCheckoutIntegrationService
  ) {}

  getAppStartParams(): AccountAppStartParams {
    const locale = this.store.selectSnapshot(FlowState.locale) as CheckoutLocale | null;
    const metadataId = this.store.selectSnapshot(FlowState.metadataId);
    const vendorId = this.store.selectSnapshot(FlowState.vendorId);
    return { locale, metadataId, vendorId };
  }

  getStartupObservable(startParams: AccountStartParams<RefreshSignInMessageParams>, context: AccountStartupContext): Observable<unknown> {
    const appStartParams = startParams.appStartParams;
    const redirectUrl = startParams.redirect;

    let appFlow: AppFlow;
    if (redirectUrl)
      appFlow = this.appRoutesService.getRedirectableFlowFromUrl(redirectUrl, appStartParams?.locale ? { locale: appStartParams.locale } : undefined);
    else if (appStartParams) appFlow = { type: 'SIGN_IN', startParams: appStartParams };
    else return throwError(() => new InvalidStateError('No app start params or redirect URL provided.'));

    return this.flowStartupService.getBaseStartupObservable(appFlow, () => this.closeWindowAndHandleAccountMigrationIfNecessary(redirectUrl, context));
  }

  private closeWindowAndHandleAccountMigrationIfNecessary(redirectUrl: string | undefined, context: AccountStartupContext): Observable<unknown> {
    return this.closeWindowIfNecessary(redirectUrl, context).pipe(switchMap(() => this.handleAccountMigrationIfNecessary(context)));
  }

  private closeWindowIfNecessary(redirectUrl: string | undefined, context: AccountStartupContext): Observable<unknown> {
    return !redirectUrl &&
      this.store.selectSnapshot(SessionState.isSignedIn) &&
      context !== 'CONFIRM_AUTHENTICATION' &&
      context !== 'OTHER' &&
      context.signInMode !== 'ON_ERROR'
      ? this.store.dispatch(new CloseWindow())
      : of(null);
  }

  private handleAccountMigrationIfNecessary(context: AccountStartupContext): Observable<unknown> {
    const accountMigration = this.store.selectSnapshot(StartupConfigurationState.accountMigration);
    if (!accountMigration || context === 'CONFIRM_AUTHENTICATION' || context === 'OTHER' || context.signInMode === 'ON_ERROR') return of(null);

    return (!this.store.selectSnapshot(LinkAccountState.accountIdLinked) ? this.store.dispatch(new SetAuthFormEmail(accountMigration.email)) : of(null)).pipe(
      switchMap(() => this.dispatchAccountMigrationNotification(accountMigration))
    );
  }

  private dispatchAccountMigrationNotification(accountMigration: AccountMigration): Observable<unknown> {
    const launchDate: DateParams | null = accountMigration.launchDate ? { date: accountMigration.launchDate, options: { dateStyle: 'short' } } : null;

    // TODO: This is custom code for the vendor NVV and must be removed in future, also the notification messages!
    if (this.store.selectSnapshot(FlowState.vendorId) === 'd8e9993d-38fa-4d3f-b86e-f770ba6bd5f3') {
      return this.store.dispatch(
        new ShowInfoNotification(
          launchDate && new Date(launchDate.date) > new Date() ? MSG_ACCOUNT_MIGRATION_NVV_WITH_LAUNCH_DATE(launchDate) : MSG_ACCOUNT_MIGRATION_NVV_FROM_NOW
        )
      );
    }
    // End of TODO

    const vendorName: string = this.store.selectSnapshot(StartupConfigurationState.vendorName) ?? '';
    return this.store.dispatch(
      new ShowInfoNotification(
        launchDate && new Date(launchDate.date) > new Date()
          ? MSG_ACCOUNT_MIGRATION_WITH_LAUNCH_DATE(vendorName, launchDate)
          : MSG_ACCOUNT_MIGRATION_FROM_NOW(vendorName)
      )
    );
  }

  adjustRedirectUrl(url: string): string {
    return this.appRoutesService.adjustToFlowStartupUrl(url);
  }

  getRouteParent(): string {
    return '/authentication';
  }

  getGotoActionReplaceUrlDefault(): Observable<boolean> {
    return this.store.select(LoadingState.isLoading);
  }

  isApplicationVisible(): Observable<boolean> {
    return this.webCheckoutIntegrationService.isApplicationVisible();
  }

  closeApplicationOnSignOut(): boolean {
    return true;
  }

  finishAuthenticationWithoutRedirection(context: AuthenticationContext, clearNotifications?: false): Observable<unknown> {
    return context === 'SIGN_UP' || this.store.selectSnapshot(StartupConfigurationState.accountMigration) !== null
      ? of(null).pipe(
          this.filterForDisabledUser(),
          switchMap(() => this.store.dispatch(new GotoRegistrationComplete({ clearNotifications, queryParams: { context } }))),
          filterForUpdatedTerms(this.store, false)
        )
      : this.prepareFlowAndCloseWindow();
  }

  adjustConfirmSignUpSuccessNotification(redirectUrl: string): Observable<unknown> {
    if (redirectUrl?.includes('COMMERCIAL_CHECKOUT')) {
      return this.store.dispatch(new ShowSuccessNotification(MSG_COMMERCIAL_CHECKOUT_SIGN_UP_SUCCESS));
    } else if (redirectUrl?.includes('ACADEMIC_REGISTRATION')) {
      return this.store.dispatch(new ShowSuccessNotification(MSG_ACADEMIC_CONFIRM_SIGN_UP_SUCCESS));
    } else {
      return of(null);
    }
  }

  finishConfirmAuthenticationEmailWithoutRedirection(context: AuthenticationContext): Observable<unknown> {
    return context === 'SIGN_UP' || this.store.selectSnapshot(StartupConfigurationState.accountMigration) !== null
      ? of(null).pipe(
          this.filterForDisabledUser(),
          switchMap(() => this.store.dispatch(new ClearNotifications())),
          filterForUpdatedTerms(this.store, false)
        )
      : this.prepareFlowAndCloseWindow();
  }

  private prepareFlowAndCloseWindow(): Observable<unknown> {
    return this.store.dispatch(new PrepareFlow()).pipe(
      this.filterForDisabledUser(),
      filterForUpdatedTerms(this.store, true),
      switchMap(() => this.store.dispatch(new CloseWindow()))
    );
  }

  finishResetPasswordWithoutRedirection(): Observable<unknown> {
    return this.finishResetOrSetPasswordWithoutRedirection(new GotoResetPasswordDone());
  }

  finishSetPasswordWithoutRedirection(): Observable<unknown> {
    return this.finishResetOrSetPasswordWithoutRedirection(new GotoSetPasswordDone());
  }

  private finishResetOrSetPasswordWithoutRedirection(action: AccountGotoAction): Observable<unknown> {
    return this.store.dispatch(new PrepareFlow()).pipe(
      this.filterForDisabledUser(),
      switchMap(() => this.store.dispatch(action)),
      filterForUpdatedTerms(this.store, false)
    );
  }

  private filterForDisabledUser(): MonoTypeOperatorFunction<unknown> {
    return obs$ =>
      obs$.pipe(
        switchMap(() => this.disabledAccountRedirectService.redirectDisabledUser(false, undefined)),
        filter(userRedirected => !userRedirected)
      );
  }

  getOAuthPopupService(): IOAuthPopupService {
    return this.oAuthPopupService;
  }

  getAccountCustomizedTexts(): AccountCustomizedTexts {
    return CHECKOUT_ACCOUNT_CUSTOMIZED_TEXTS(
      this.store.selectSnapshot(FlowState.flow),
      this.webCheckoutIntegrationService.getVendorType() === 'EXTERNAL' ? 'unknown' : 'yatta'
    );
  }

  getOrderedOAuthProviders(): Observable<OAuthProvider[]> {
    return this.store.select(StartupConfigurationState.orderedOAuthProviders);
  }

  getSuccessLottiePath(): string | null {
    return PLATFORM_ENVIRONMENT.webshopUrl + '/assets/static/lottie/success-default-lottie.json';
  }

  setHeaderDataField(route: ActivatedRouteSnapshot): string | null {
    return route.data.isRegistrationCompletePage || route.data.isUpdatePasswordDonePage || route.data.isNewTermsPage ? 'none' : 'simple';
  }
}
